import React, { useState, useEffect, useContext, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Button, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, IconButton, Divider, CircularProgress, Box, Tooltip,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import CloseIcon from '@material-ui/icons/Close';
import CheckIcon from '@material-ui/icons/Check';
import AlarmIcon from '@material-ui/icons/Alarm';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { AuthContext } from '../../context/Auth/AuthContext';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import { hasRolloutFeature, ROLLOUT_FEATURES } from '../../utils/rolloutFeatures';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: theme.spacing(3),
    gap: theme.spacing(2),
    overflow: 'auto',
    boxSizing: 'border-box',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
  },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: 800,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
  },
  groupLabel: {
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '1.2px',
    textTransform: 'uppercase',
    padding: '6px 0 2px 4px',
    marginTop: 8,
  },
  card: {
    borderRadius: 10,
    border: '1px solid',
    marginBottom: 6,
    padding: '10px 12px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
  },
}));

const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const defaultDateTime = () => {
  const d = new Date();
  d.setHours(d.getHours() + 1, 0, 0, 0);
  return toLocalInput(d.toISOString());
};

const emptyForm = () => ({ title: '', notes: '', dateTime: defaultDateTime(), ticketId: '', contactId: '', contactName: '' });

const Reminders = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const history = useHistory();
  const remindersV2 = hasRolloutFeature(user, ROLLOUT_FEATURES.REMINDERS_V2);

  const [loading, setLoading] = useState(true);
  const [reminders, setReminders] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  // Contact search
  const [contactOptions, setContactOptions] = useState([]);
  const [contactSearching, setContactSearching] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/reminders');
      setReminders(res.data || []);
    } catch { toast.error('Erro ao carregar lembretes'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const searchContacts = async (query) => {
    if (!query || query.length < 2) { setContactOptions([]); return; }
    setContactSearching(true);
    try {
      const res = await api.get('/contacts', { params: { searchParam: query, pageNumber: 1 } });
      setContactOptions(res.data.contacts || []);
    } catch { setContactOptions([]); }
    finally { setContactSearching(false); }
  };

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm());
    setSelectedContact(null);
    setContactOptions([]);
    setOpen(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      title: r.title || '',
      notes: r.notes || '',
      dateTime: toLocalInput(r.dateTime),
      ticketId: r.ticketId || '',
      contactId: r.contactId || '',
      contactName: r.contactName || '',
    });
    setSelectedContact(r.contactName ? { id: r.contactId, name: r.contactName } : null);
    setOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error('Informe o título');
    if (!form.dateTime) return toast.error('Informe a data e hora');
    setSaving(true);
    try {
      const payload = {
        ...form,
        contactId: selectedContact ? selectedContact.id : (form.contactId || null),
        contactName: selectedContact ? selectedContact.name : (form.contactName || null),
      };
      if (editing) {
        await api.put(`/reminders/${editing.id}`, { ...payload, status: editing.status });
        toast.success('Lembrete atualizado!');
      } else {
        await api.post('/reminders', payload);
        toast.success('Lembrete criado!');
      }
      setOpen(false);
      await load();
    } catch { toast.error('Erro ao salvar lembrete'); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/reminders/${id}`); await load(); }
    catch { toast.error('Erro ao remover'); }
  };

  const markDone = async (id) => {
    try { await api.post(`/reminders/${id}/done`); await load(); }
    catch { toast.error('Erro'); }
  };

  const openReminder = async (reminder) => {
    if (!remindersV2) {
      if (reminder.ticketId) history.push(`/tickets/${reminder.ticketId}`);
      return;
    }
    try {
      const { data } = await api.post(`/reminders/${reminder.id}/view`);
      if (data?.target?.ticketUuid) {
        history.push(`/tickets/${data.target.ticketUuid}`);
        return;
      }
      toast.error('A conversa associada a este lembrete não existe mais.');
      await load();
    } catch {
      toast.error('Não foi possível abrir a conversa deste lembrete.');
    }
  };

  const now = new Date();
  const todayEnd = new Date(); todayEnd.setHours(23, 59, 59, 999);

  const alerted = reminders.filter(r => r.status === 'notified');
  const overdue = reminders.filter(r => r.status === 'pending' && new Date(r.dateTime) < now);
  const today   = reminders.filter(r => r.status === 'pending' && new Date(r.dateTime) >= now && new Date(r.dateTime) <= todayEnd);
  const future  = reminders.filter(r => r.status === 'pending' && new Date(r.dateTime) > todayEnd);
  const done    = reminders.filter(r => r.status === 'done' || r.status === 'dismissed');

  const fmt = (iso) => {
    const d = new Date(iso);
    return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const ReminderCard = ({ r, borderColor, bgColor }) => (
    <Box className={classes.card} style={{ borderColor, background: bgColor }}>
      <AlarmIcon style={{ color: borderColor, flexShrink: 0, fontSize: 20, marginTop: 2 }} />
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" style={{ fontWeight: 700 }}>{r.title}</Typography>
        <Typography variant="caption" color="textSecondary">
          {fmt(r.dateTime)}
          {r.contactName && <span style={{ marginLeft: 8 }}>• {r.contactName}</span>}
        </Typography>
        {r.notes && (
          <Typography variant="caption" color="textSecondary" style={{ display: 'block', opacity: 0.8 }}>
            {r.notes}
          </Typography>
        )}
      </Box>
      <Box style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
        {(r.ticketId || (remindersV2 && r.contactId)) && (
          <Tooltip title="Abrir conversa">
            <IconButton size="small" onClick={() => openReminder(r)}>
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {(r.status === 'pending' || r.status === 'dismissed') && (
          <Tooltip title="Marcar como concluído">
            <IconButton size="small" onClick={() => markDone(r.id)}>
              <CheckIcon fontSize="small" style={{ color: '#22c55e' }} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Editar">
          <IconButton size="small" onClick={() => openEdit(r)}><EditIcon fontSize="small" /></IconButton>
        </Tooltip>
        <Tooltip title="Remover">
          <IconButton size="small" onClick={() => remove(r.id)}><DeleteIcon fontSize="small" /></IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <div className={classes.root}>
      <div className={classes.sectionHeader}>
        <Typography className={classes.sectionTitle} style={{ color: '#00875A' }}>
          Meus Lembretes
        </Typography>
        <Button variant="contained" color="primary" size="small" startIcon={<AddIcon />}
          onClick={openNew} style={{ borderRadius: 8, textTransform: 'none', fontWeight: 700 }}>
          + Novo Lembrete
        </Button>
      </div>

      <Divider />

      {loading ? (
        <Box style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
          <CircularProgress size={28} />
        </Box>
      ) : reminders.length === 0 && alerted.length === 0 ? (
        <Box style={{ textAlign: 'center', padding: 48, opacity: 0.45 }}>
          <AlarmIcon style={{ fontSize: 52, marginBottom: 8 }} />
          <Typography>Nenhum lembrete criado ainda.</Typography>
          <Typography variant="caption">Clique em "+ Novo Lembrete" para começar.</Typography>
        </Box>
      ) : (
        <Box>
          {alerted.length > 0 && (
            <>
              <Box style={{
                background: 'linear-gradient(135deg, rgba(239,68,68,0.12) 0%, rgba(239,68,68,0.06) 100%)',
                border: '2px solid #ef4444',
                borderRadius: 10,
                padding: '10px 12px',
                marginBottom: 8,
              }}>
                <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 18 }}>🔔</span>
                  <Typography style={{ color: '#ef4444', fontWeight: 800, fontSize: '0.75rem', letterSpacing: '1px', textTransform: 'uppercase' }}>
                    Lembrete disparado — aguardando confirmação ({alerted.length})
                  </Typography>
                </Box>
                {alerted.map(r => (
                  <Box key={r.id} style={{
                    background: 'rgba(239,68,68,0.1)',
                    border: '1.5px solid rgba(239,68,68,0.4)',
                    borderRadius: 8,
                    padding: '10px 12px',
                    marginBottom: 6,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                  }}>
                    <AlarmIcon style={{ color: '#ef4444', flexShrink: 0, fontSize: 20, marginTop: 2 }} />
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" style={{ fontWeight: 800, color: '#ef4444' }}>{r.title}</Typography>
                      <Typography variant="caption" style={{ color: '#991b1b' }}>
                        {fmt(r.dateTime)}
                        {r.contactName && <span style={{ marginLeft: 8 }}>• {r.contactName}</span>}
                      </Typography>
                      {r.notes && (
                        <Typography variant="caption" style={{ display: 'block', color: '#7f1d1d', opacity: 0.9 }}>
                          {r.notes}
                        </Typography>
                      )}
                    </Box>
                    <Box style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                      {(r.ticketId || (remindersV2 && r.contactId)) && (
                        <Tooltip title="Abrir conversa">
                          <IconButton size="small" onClick={() => openReminder(r)}>
                            <OpenInNewIcon fontSize="small" style={{ color: '#ef4444' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Marcar como visto (remove o destaque)">
                        <IconButton size="small" onClick={() => markDone(r.id)}
                          style={{ background: '#ef4444', borderRadius: 6, padding: 4 }}>
                          <CheckIcon fontSize="small" style={{ color: '#fff', fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remover">
                        <IconButton size="small" onClick={() => remove(r.id)}>
                          <DeleteIcon fontSize="small" style={{ color: '#ef4444' }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                ))}
              </Box>
            </>
          )}

          {overdue.length > 0 && (
            <>
              <Typography className={classes.groupLabel} style={{ color: '#ef4444' }}>
                Atrasados ({overdue.length})
              </Typography>
              {overdue.map(r => <ReminderCard key={r.id} r={r} borderColor="#ef4444" bgColor="rgba(239,68,68,0.05)" />)}
            </>
          )}
          {today.length > 0 && (
            <>
              <Typography className={classes.groupLabel} style={{ color: '#f59e0b' }}>
                Hoje ({today.length})
              </Typography>
              {today.map(r => <ReminderCard key={r.id} r={r} borderColor="#f59e0b" bgColor="rgba(245,158,11,0.05)" />)}
            </>
          )}
          {future.length > 0 && (
            <>
              <Typography className={classes.groupLabel} style={{ color: '#3b82f6' }}>
                Futuros ({future.length})
              </Typography>
              {future.map(r => <ReminderCard key={r.id} r={r} borderColor="#3b82f6" bgColor="rgba(59,130,246,0.05)" />)}
            </>
          )}
          {done.length > 0 && (
            <>
              <Typography className={classes.groupLabel} style={{ color: '#9ca3af' }}>
                Concluídos ({done.length})
              </Typography>
              {done.map(r => <ReminderCard key={r.id} r={r} borderColor="#d1d5db" bgColor="rgba(156,163,175,0.04)" />)}
            </>
          )}
        </Box>
      )}

      {/* Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ style: { borderRadius: 12 } }}>
        <DialogTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlarmIcon style={{ color: '#f59e0b' }} />
            <span style={{ fontWeight: 700 }}>{editing ? 'Editar Lembrete' : 'Novo Lembrete'}</span>
          </Box>
          <IconButton size="small" onClick={() => setOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <TextField label="Título *" fullWidth variant="outlined" size="small"
              value={form.title} onChange={e => { const v = e.target.value; setForm(p => ({ ...p, title: v })); }}
              placeholder="Ex: Ligar para cliente, Enviar proposta..."/>

            <TextField label="Data e Hora *" type="datetime-local" fullWidth variant="outlined" size="small"
              value={form.dateTime} onChange={e => { const v = e.target.value; setForm(p => ({ ...p, dateTime: v })); }}
              InputLabelProps={{ shrink: true }} />

            <TextField label="Observações" fullWidth variant="outlined" size="small" multiline rows={2}
              value={form.notes} onChange={e => { const v = e.target.value; setForm(p => ({ ...p, notes: v })); }}
              placeholder="Detalhes opcionais..." />

            {/* Vincular contato — opcional */}
            <Autocomplete
              options={contactOptions}
              getOptionLabel={o => o.name + (o.number ? ` (${o.number})` : '')}
              loading={contactSearching}
              value={selectedContact}
              onChange={(_, val) => {
                setSelectedContact(val);
                if (!val) setForm(p => ({ ...p, contactId: '', contactName: '' }));
              }}
              onInputChange={(_, val) => searchContacts(val)}
              noOptionsText="Digite para buscar contato..."
              renderInput={params => (
                <TextField {...params} label="Vincular a um contato (opcional)" variant="outlined" size="small"
                  placeholder="Buscar contato pelo nome ou número..." />
              )}
            />

            {(form.ticketId) && (
              <Box style={{ background: 'rgba(0,135,90,0.08)', border: '1px solid rgba(0,135,90,0.25)', borderRadius: 8, padding: '8px 12px' }}>
                <Typography variant="caption" style={{ color: '#00875A', fontWeight: 600 }}>
                  Vinculado ao Ticket #{form.ticketId}
                  {form.contactName && ` — ${form.contactName}`}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions style={{ padding: '12px 24px' }}>
          <Button onClick={() => setOpen(false)} variant="outlined"
            style={{ borderRadius: 8, textTransform: 'none' }}>
            Fechar
          </Button>
          <Button onClick={save} variant="contained" color="primary" disabled={saving}
            style={{ borderRadius: 8, textTransform: 'none', fontWeight: 700 }}>
            {saving ? 'Salvando...' : editing ? 'Salvar' : 'Criar Lembrete'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Reminders;
